import { lazy, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import { Stack } from '@mui/material';

import { LazyLoadedComponent } from 'app-zephyr-components/LazyLoadedComponent';
import { AddCircleIcon } from 'app-zephyr-icons';
import { Button } from 'app-zephyr-components/Button';
import { useCreateOrganization } from 'app-zephyr-domains/organization/create-organization';
import { useCurrentUser } from 'app-zephyr-domains/user';
import { organization_path } from 'app-zephyr-routes';
import { darkThemeOptions as theme } from 'app-zephyr-styles/themes';

const AddOrganizationModal = lazy(() => import('app-zephyr-components/AddOrganizationModal'));
const AlphaAcceptModal = lazy(() => import('app-zephyr-components/AlphaAcceptModal'));

interface CreateNewBtnProps {
  onBtnClick?: () => void;
  navigateStarted?: () => void;
}

//todo: button should be seperated into component, cause there will be the same one for notifications
export function CreateNewBtn({ onBtnClick, navigateStarted }: CreateNewBtnProps) {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const { mutateAsync: createOrganization } = useCreateOrganization();
  const [openNewOrgModal, setOpenNewOrgModal] = useState(false);
  const [openAcceptModal, setOpenAcceptModal] = useState(false);

  const alphaAcceptModal = () => {
    setOpenAcceptModal(true);
  };

  const onAlphaAcceptModalConfirm = () => {
    setOpenAcceptModal(false);
    setTimeout(() => {
      setOpenNewOrgModal(true);
    }, 200);
  };

  const onCreateOrgSave = async (organizationDisplayName: string, organizationName: string) => {
    if (!user) return;

    if (!organizationName || !organizationDisplayName) {
      Sentry.captureMessage(
        `Organization name or display name is empty: ${organizationName} ${organizationDisplayName}`,
        {
          level: 'warning',
          user,
          tags: { onCreateOrgSave: 'emptyName' },
        },
      );
      return;
    }

    const _newOrg = {
      name: organizationName.trim(),
      displayName: organizationDisplayName.trim(),
      acceptedBy: user.id,
    };
    const res = await createOrganization(_newOrg);

    if (res.data) {
      closeCreateOrgMenu();
      if (navigateStarted) navigateStarted();

      void navigate({
        to: organization_path({ organization: _newOrg }),
        search: (p: never) => p,
        params: (p: never) => p,
      });
    }
  };

  const closeCreateOrgMenu = () => {
    setOpenNewOrgModal(false);
  };

  const onClose = () => {
    setOpenNewOrgModal(false);
    setOpenAcceptModal(false);
  };

  const dashboardNavigate = () => {
    onClose();
    void navigate({ to: '/', search: (p: never) => p, params: (p: never) => p });
  };

  return (
    <>
      <Stack
        padding={'24px 0'}
        alignItems={'center'}
        borderTop={`1px solid ${theme.palette.brand.gray[800] ?? '#242424'}`}
      >
        <Button
          endIcon={<AddCircleIcon />}
          onClick={() => {
            onBtnClick && onBtnClick();
            alphaAcceptModal();
          }}
        >
          Create New
        </Button>
      </Stack>

      <LazyLoadedComponent>
        {openNewOrgModal && <AddOrganizationModal open={openNewOrgModal} onClose={onClose} onSave={onCreateOrgSave} />}
      </LazyLoadedComponent>

      <LazyLoadedComponent>
        {openAcceptModal && (
          <AlphaAcceptModal
            open={openAcceptModal}
            onLogOut={() => logout()}
            onChangeOrg={dashboardNavigate}
            onConfirm={onAlphaAcceptModalConfirm}
          />
        )}
      </LazyLoadedComponent>
    </>
  );
}
